export type DisplaySeatStatus = 'active' | 'paused';
export type WakeLockState = 'held' | 'released' | 'refused' | 'unavailable';

export interface WakeLockHandle {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
  removeEventListener(type: 'release', listener: () => void): void;
}

export interface VisibilitySource {
  readonly visibilityState: 'visible' | 'hidden' | 'prerender';
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface DisplaySeatLifecycle {
  ready: Promise<void>;
  syncVisibility(): Promise<void>;
  stop(): void;
}

export function startDisplaySeatLifecycle(options: {
  visibility: VisibilitySource;
  requestWakeLock?: () => Promise<WakeLockHandle>;
  sendStatus: (status: DisplaySeatStatus) => void;
  onWakeLockState?: (state: WakeLockState) => void;
}): DisplaySeatLifecycle {
  let stopped = false;
  let generation = 0;
  let lastStatus: DisplaySeatStatus | undefined;
  let wakeLock: WakeLockHandle | undefined;
  let wakeRequest: Promise<void> | undefined;

  const publish = (status: DisplaySeatStatus): void => {
    if (lastStatus === status || stopped) return;
    lastStatus = status;
    options.sendStatus(status);
  };

  const releaseWakeLock = (): void => {
    const held = wakeLock;
    wakeLock = undefined;
    if (held) void held.release().catch(() => undefined);
  };

  const acquireWakeLock = (): Promise<void> => {
    if (stopped || options.visibility.visibilityState !== 'visible' || wakeLock) return Promise.resolve();
    if (!options.requestWakeLock) {
      options.onWakeLockState?.('unavailable');
      return Promise.resolve();
    }
    if (wakeRequest) return wakeRequest;
    const requestedGeneration = generation;
    wakeRequest = (async () => {
      try {
        const acquired = await options.requestWakeLock!();
        if (stopped || requestedGeneration !== generation || options.visibility.visibilityState !== 'visible') {
          await acquired.release().catch(() => undefined);
          return;
        }
        const onRelease = (): void => {
          acquired.removeEventListener('release', onRelease);
          const wasCurrent = wakeLock === acquired;
          if (wasCurrent) wakeLock = undefined;
          options.onWakeLockState?.('released');
          // Browsers may revoke a sentinel while still visible (for example under power pressure).
          // Explicit hidden/stop releases clear wakeLock before calling release(), so they do not loop.
          if (wasCurrent && !stopped && options.visibility.visibilityState === 'visible') void acquireWakeLock();
        };
        acquired.addEventListener('release', onRelease);
        wakeLock = acquired;
        options.onWakeLockState?.('held');
      } catch {
        if (!stopped && requestedGeneration === generation) options.onWakeLockState?.('refused');
      } finally {
        wakeRequest = undefined;
        // A quick hidden→visible transition can outlive the old request. Acquire for the new generation.
        if (!stopped && requestedGeneration !== generation && options.visibility.visibilityState === 'visible' && !wakeLock) {
          void acquireWakeLock();
        }
      }
    })();
    return wakeRequest;
  };

  const syncVisibility = async (): Promise<void> => {
    if (stopped) return;
    if (options.visibility.visibilityState !== 'visible') {
      generation += 1;
      publish('paused');
      releaseWakeLock();
      return;
    }
    publish('active');
    await acquireWakeLock();
  };

  const onVisibilityChange = (): void => { void syncVisibility(); };
  options.visibility.addEventListener('visibilitychange', onVisibilityChange);
  const ready = syncVisibility();

  return {
    ready,
    syncVisibility,
    stop: () => {
      if (stopped) return;
      stopped = true;
      generation += 1;
      options.visibility.removeEventListener('visibilitychange', onVisibilityChange);
      releaseWakeLock();
    },
  };
}
